import { Effect, Option, type Predicate } from 'effect';

import type { OrderToken } from '../../service/orderToken/OrderToken';
import { Cart } from './Cart';
import { heldToken } from './cartToken';
import type { Cart as CartView } from './cartSchema';
import { CartError } from './errors';
import type { CartError as CartReadError, CartUnreadable } from './errors';

export type HeldCartSnapshot<S> = {
  readonly hadToken: boolean;
  readonly value: Option.Option<S>;
};

/** Apply one token and missing-cart rule to a tenant's confirmed snapshot read. */
export const readHeldCartSnapshot = <S, E, M extends E, R, RMissing, EToken, RToken>({
  token,
  read,
  isMissing,
  onMissing,
}: {
  readonly token: Effect.Effect<Option.Option<string>, EToken, RToken>;
  readonly read: (token: string) => Effect.Effect<S, E, R>;
  readonly isMissing: Predicate.Refinement<E, M>;
  readonly onMissing: Effect.Effect<void, never, RMissing>;
}): Effect.Effect<HeldCartSnapshot<S>, Exclude<E, M> | EToken, RToken | R | RMissing> =>
  token.pipe(
    Effect.flatMap(
      Option.match({
        onNone: () => Effect.succeed({ hadToken: false, value: Option.none<S>() }),
        onSome: (held) =>
          read(held).pipe(
            Effect.map((value) => ({ hadToken: true, value: Option.some(value) })),
            Effect.catchIf(isMissing, () =>
              onMissing.pipe(Effect.as({ hadToken: true, value: Option.none<S>() }))
            )
          ),
      })
    )
  );

/**
 * Read the held cart without creating one.
 * A missing cart invokes the caller's stale token policy and resolves to `None`.
 * Other cart errors retain the token and reach the caller.
 */
export const readCurrentCart = <R>(
  onMissing: Effect.Effect<void, never, R>
): Effect.Effect<Option.Option<CartView>, CartUnreadable, Cart | OrderToken | R> =>
  readHeldCartSnapshot({
    token: heldToken,
    read: (token: string): Effect.Effect<CartView, CartReadError, Cart> =>
      Cart.pipe(Effect.flatMap((cart) => cart.cart(token))),
    isMissing: (error: CartReadError): error is InstanceType<typeof CartError.NotFound> =>
      error instanceof CartError.NotFound,
    onMissing,
  }).pipe(Effect.map((snapshot) => snapshot.value));
